// BulkFeeModal — S8.1 "Generate Monthly Fees for All Owners". Sprint 5c.
//
// Owns its own Form instance; the orchestrator passes the form ref via
// `formRef` so the bulk-generate handler can call validateFields() before
// submitting. Cleaner than threading a Form instance from above through
// props.
import React from 'react';
import {
  Alert, Col, Form, Input, InputNumber, Modal, Row, Select, Space,
} from 'antd';
import { ThunderboltOutlined } from '@ant-design/icons';
import type { FormInstance } from 'antd';

export interface BulkFeeModalProps {
  open: boolean;
  ownerCount: number;
  isGenerating: boolean;
  formRef: FormInstance;
  onCancel: () => void;
  onConfirm: () => void;
}

export const BulkFeeModal: React.FC<BulkFeeModalProps> = ({
  open, ownerCount, isGenerating, formRef, onCancel, onConfirm,
}) => (
  <Modal
    title={<Space><ThunderboltOutlined style={{ color: '#722ed1' }} />Generate Monthly Fees for All Owners</Space>}
    open={open}
    onCancel={onCancel}
    onOk={onConfirm}
    okText={`Generate for ${ownerCount} owner${ownerCount === 1 ? '' : 's'}`}
    confirmLoading={isGenerating}
    width={540}
    destroyOnHidden
  >
    <Alert
      type="info"
      showIcon
      message="This will create one fee for every owner in the current condo."
      style={{ marginBottom: 16 }}
    />
    {/* Sprint 139 — preserve={false} so that when destroyOnHidden unmounts these
        fields on close, their values are dropped from the form store too. That
        gives a clean form on every reopen WITHOUT a resetFields() call on the
        (then-unmounted) instance, which was firing the "useForm is not connected
        to any Form element" warning from the parent. */}
    <Form form={formRef} layout="vertical" preserve={false}>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="feeType" label="Fee Type" rules={[{ required: true }]} initialValue="MAINTENANCE">
            <Select>
              <Select.Option value="MAINTENANCE">Maintenance</Select.Option>
              <Select.Option value="SPECIAL_ASSESSMENT">Special Assessment</Select.Option>
              <Select.Option value="OTHER">Other</Select.Option>
            </Select>
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="amount" label="Amount per Owner ($)" rules={[{ required: true }]}>
            <InputNumber min={0} precision={2} style={{ width: '100%' }} addonBefore="$" />
          </Form.Item>
        </Col>
      </Row>
      <Row gutter={16}>
        <Col span={12}>
          <Form.Item name="dueDate" label="Due Date" rules={[{ required: true }]}>
            <Input type="date" />
          </Form.Item>
        </Col>
        <Col span={12}>
          <Form.Item name="period" label="Period (e.g. 2026-05)">
            <Input placeholder="YYYY-MM" />
          </Form.Item>
        </Col>
      </Row>
      <Form.Item name="description" label="Description" rules={[{ required: true }]}>
        <Input placeholder="Monthly maintenance fee — May 2026" />
      </Form.Item>
    </Form>
  </Modal>
);
